import { useCallback, useState } from "react";
import type { Moment } from "moment";
import { taskService } from "@miplatform/api";
import { ATTEMPT_STATUSES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import {
  CalendarEventType,
  CalendarType,
  IApiRequest,
  IApiResponse,
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
  IAttemptTrayConfig,
  IAttemptUpdateSubscriptionPayload,
  ICalendarConfig,
  ICalendarSet,
  IDateRangeISO,
  IUserListItemResponse,
} from "@miplatform/types";
import {
  addWorkDays,
  bugsnag,
  convertArrayToMap,
  createUUID,
  getDate,
  getDateISO,
  getGUIDArray,
  isSameOrAfter,
  isSameOrBefore,
} from "@miplatform/utils";
import { loadCalendarEvents } from "./utils/loadCalendarEvents";
import { useStoredCalendar, useStoredCalendarSet } from "../../stores";
import { FOLLOW_UP_TRAY_FILTERS } from "../../views/Config/Calendars/ConfigCalendarSetList.constants";
import { CalendarAttemptEvents } from "./CalendarProvider.types";

const loadEventsUUID = createUUID();
const SELECTED_USER_DAYS_RANGE = 5;

const getStartDate = ({
  calendarType,
  date,
  dateRange,
}: {
  calendarType: CalendarType | undefined;
  date: Moment;
  dateRange: IDateRangeISO;
}) => {
  if (calendarType === CalendarType.Kanban) {
    return getDate(dateRange.startDate);
  }

  return date;
};

const getEndDate = ({
  calendarType,
  date,
  dateRange,
  isUserSelected,
}: {
  calendarType: CalendarType | undefined;
  date: Moment;
  dateRange: IDateRangeISO;
  isUserSelected: boolean;
}) => {
  if (calendarType === CalendarType.Kanban) {
    return getDate(dateRange.endDate);
  }

  if (isUserSelected) {
    return addWorkDays(SELECTED_USER_DAYS_RANGE, date);
  }

  return undefined;
};

export const useCalendarProviderViewModel = ({
  calendarSetId = null,
  calendarId = null,
  selectedUser = null,
}: IUseCalendarProviderViewModelProps) => {
  const stateManager = useComponentState();

  const { calendarSet } = useStoredCalendarSet(calendarSetId);

  const { calendarConfig } = useStoredCalendar(calendarSetId, calendarId);
  const { config: calendarSetConfig } = calendarSet ?? {};
  const { assignableUsers = [], schedulerUsers = [] } = calendarSetConfig ?? {};

  const activeCalendarId = calendarConfig?.id;

  const [events, setEvents] = useState<CalendarAttemptEvents>({});
  const [selectedAttempt, setSelectedAttempt] = useState<IAttempt | null>(null);
  const [zoom, setZoom] = useState(1);
  const [date, setDate] = useState<Moment>(getDate());
  const [dateRange, setDateRange] = useState<IDateRangeISO>({
    startDate: getDateISO(getDate()),
    endDate: getDateISO(getDate()),
  });
  const [trayBuckets, setTrayBuckets] = useState<IAttemptTrayConfig[]>([]);
  const [allTrayEvents, setAllTrayEvents] = useState<
    IAttemptListResponse[] | IAttemptListItemResponseForWorkOrder[]
  >([]);
  const [calendarCounts, setCalendarCounts] = useState<Record<string, number>>(
    {}
  );
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const onDateRangeChange = (newDateRange: IDateRangeISO) => {
    setDateRange(newDateRange);
  };

  /**
   * TODO: Improve the speed of this getTrays call. Super slow. On average I am seeing 4+ seconds to load
   * const startTime = performance.now();
   * await getTrayDataForCalendar();
   * console.log(
   *  `Took ${(performance.now() - startTime).toFixed(2)}ms to get Trays`
   * )
   *
   * Unified tray data fetching - used by both Kanban buckets and HoldsList
   * @returns void
   */
  const getTrayDataForCalendar = async () => {
    if (
      !calendarSet?.config?.trays?.length ||
      !calendarSet?.config.calendarConfigs.length
    ) {
      setTrayBuckets([]);
      setAllTrayEvents([]);
      return;
    }

    const attachedTrayCalendarMap = Object.values(calendarSet.calendars).reduce(
      (acc: Record<string, string[]>, calendar) => {
        if (calendar.config.attachedTray) {
          if (!acc[calendar.config.attachedTray.id]) {
            acc[calendar.config.attachedTray.id] = [];
          }
          acc[calendar.config.attachedTray.id].push(calendar.config.id);
        }
        return acc;
      },
      {}
    );

    const orderTemplateIds =
      calendarSet.config.workTemplateBases?.map((template) => template.id) ??
      [];

    const trayBuckets: IAttemptTrayConfig[] = [];
    const allTrayData:
      | IAttemptListResponse[]
      | IAttemptListItemResponseForWorkOrder[] = [];
    const isWorkOrderEventType =
      calendarSet.config.eventType === CalendarEventType.WorkOrder;
    const assignmentPath = isWorkOrderEventType ? "workOrder" : "order";
    const templateIdPath = isWorkOrderEventType
      ? "workOrderTemplateId"
      : "orderTemplateId";
    const getAttempts = isWorkOrderEventType
      ? taskService.attempts.getAllWorkOrders
      : taskService.attempts.getAll;

    const trayPromises = calendarSet.config.trays.map(
      async (tray: IAttemptTrayConfig) => {
        const filterStringParts = [];
        if (tray?.filter) {
          filterStringParts.push(`(${tray?.filter})`);
        }

        if (orderTemplateIds.length) {
          filterStringParts.push(
            `assignment/${assignmentPath}/${templateIdPath} in (${orderTemplateIds.join(
              ","
            )})`
          );
        }

        // TODO : yuck, but elsewhere we are matching on filter to determine type like in the "Tray Type" dropdown in calendar configs
        // this will filter all Follow-Up trays to limit them to the attached Hold Reason Tabs/Calendar Ids, to prevent
        // trays that have no attached calendars (hold reasons) from finding orders from ALL calendars instead of none.
        if (tray?.filter === FOLLOW_UP_TRAY_FILTERS) {
          const trayAttachedCalendarIds =
            attachedTrayCalendarMap[tray.id] ?? [];
          if (trayAttachedCalendarIds.length < 1) {
            // a follow up tray with no attached calendars
            // this tray will always be empty, so skip the API call
            return {
              tray,
              data: [],
            };
          }
          filterStringParts.push(
            `assignment/${assignmentPath}/holdCalendarId in (${trayAttachedCalendarIds.join(
              ","
            )})`
          );
        }

        const filterString = filterStringParts.join(" and ");

        try {
          const response = await getAttempts({
            filters: filterString,
            ...(!isWorkOrderEventType && {
              params: {
                includeWorkOrders: true,
              },
            }),
            ...(tray.sort &&
              typeof tray.sort === "string" && { sort: tray.sort }),
          });

          if (response.status === HTTP_STATUSES.OK) {
            return {
              tray,
              data: response.data.map((attempt: IAttemptListResponse) => {
                return {
                  ...attempt,
                  isRequestedInDateRange:
                    attempt.assignment?.order?.requestedStartDate &&
                    isSameOrAfter(
                      attempt.assignment?.order?.requestedStartDate,
                      dateRange.startDate
                    ) &&
                    isSameOrBefore(
                      attempt.assignment?.order?.requestedStartDate,
                      dateRange.endDate ?? dateRange.startDate
                    ),
                };
              }),
            };
          }
        } catch (trayError) {
          bugsnag.setContext(
            "useCalendarProviderViewModel::getTrayDataForCalendar"
          );
          bugsnag.notify(
            trayError instanceof Error
              ? trayError
              : new Error(String(trayError))
          );
        }

        return {
          tray,
          data: [],
        };
      }
    );

    try {
      const trayResults = await Promise.all(trayPromises);
      trayResults.forEach(({ tray, data }) => {
        allTrayData.push(...data);

        trayBuckets.push({
          id: tray.id,
          isActive: tray.isActive,
          isDefault: tray.isDefault,
          name: tray.name,
          collapsible: tray.collapsible,
          displayOrder: tray.displayOrder,
          headerColor: tray.headerColor,
          filter: tray.filter,
          headerBackgroundColor: tray.headerBackgroundColor,
          contentBackgroundColor: tray.contentBackgroundColor,
          countBackgroundColor: tray.countBackgroundColor,
          countColor: tray.countColor,
          hideWhenEmpty: tray.hideWhenEmpty,
          data,
        });
      });

      trayBuckets.sort((a, b) => a.displayOrder - b.displayOrder);
    } catch (error) {
      bugsnag.setContext(
        "useCalendarProviderViewModel::getTrayDataForCalendar"
      );
      bugsnag.notify(error instanceof Error ? error : new Error(String(error)));
    }

    setTrayBuckets(trayBuckets);
    setAllTrayEvents(allTrayData);
  };

  const resetTrays = () => {
    setAllTrayEvents([]);
    setTrayBuckets([]);
  };

  const handleLoadEvents = async () => {
    stateManager.abort(loadEventsUUID);
    const startDate = getStartDate({
      calendarType: calendarConfig?.calendarType,
      date,
      dateRange,
    });
    const endDate = getEndDate({
      calendarType: calendarConfig?.calendarType,
      date,
      dateRange,
      isUserSelected: !!selectedUser,
    });

    return stateManager.run(async () => {
      setEvents({});
      if (calendarSetConfig && calendarConfig) {
        const { data: newEvents, status } = await loadCalendarEvents({
          calendarSetConfig,
          calendarConfig,
          startDate,
          endDate,
          userIds: selectedUser ? [selectedUser.id] : null,
        });

        if (status === HTTP_STATUSES.OK) {
          setEvents(convertArrayToMap(newEvents, "id"));
          getCalendarCounts(calendarSetConfig);
        }
      }
    }, loadEventsUUID);
  };

  const onAttemptsUpdate = useCallback(
    (payload: IAttemptUpdateSubscriptionPayload) => {
      setEvents((prev) => {
        const newEvents = { ...prev };
        for (const updatedEvent of payload) {
          const {
            attemptUpdatedEvent: { attemptId: updatedAttemptId },
          } = updatedEvent;

          const currentEvent = newEvents[updatedAttemptId];

          newEvents[updatedAttemptId] = {
            ...currentEvent,
            ...updatedEvent.attempt,
          };
        }

        return newEvents;
      });
    },
    [calendarSetConfig, activeCalendarId]
  );

  const setCalendarDate = (newDate: Date | string | Moment) => {
    setDate(getDate(newDate));
  };

  const handleCalendarSearch = (newValue: string) => {
    setSearch(newValue);
  };

  const getCalendarCount = async (
    calendar: ICalendarConfig,
    getter: (params: IApiRequest) => Promise<IApiResponse<IAttempt[]>>
  ) => {
    const response = await getter({
      filters: {
        ...calendar.filter,
        attemptStatus: { eq: ATTEMPT_STATUSES.HOLD },
        isOrderFollowupDue: false,
        assignment: {
          ...(calendarSetConfig && {
            [calendarSetConfig?.eventType]: {
              [`${calendarSetConfig?.eventType}TemplateId`]: {
                in: getGUIDArray(
                  (calendar.workTemplateBases || []).map(
                    (template) => template.id
                  )
                ),
              },
              holdCalendarId: {
                type: "guid",
                value: calendar.id,
              },
            },
          }),
        },
      },
      sort: "modifiedOn desc",
      top: 0,
    });
    if (response.status === HTTP_STATUSES.OK) {
      return (response.headers.totalcount as number) || 0;
    }

    return 0;
  };

  const getCalendarCounts = (calendarSet: ICalendarSet) => {
    if (!calendarSet) return;

    const getter =
      calendarSet.eventType === CalendarEventType.WorkOrder
        ? taskService.attempts.getAllWorkOrders
        : taskService.attempts.getAll;
    stateManager.run(async () => {
      const counts = await Promise.all(
        calendarSet?.calendarConfigs
          .filter(
            (calendar: ICalendarConfig) =>
              calendar.calendarType === CalendarType.HoldsList
          )
          .map(async (calendar) => {
            const count = await getCalendarCount(calendar, getter);

            return {
              id: calendar.id,
              count,
            };
          })
      );

      setCalendarCounts(
        counts.reduce((acc, { id, count }) => {
          if (count !== null) {
            acc[id] = count;
          }
          return acc;
        }, {} as Record<string, number>)
      );
    });
  };

  return {
    calendarSet,
    calendarConfig,
    events,
    assignableUsers,
    schedulerUsers,
    activeCalendarId,
    zoom,
    isLoading: stateManager.isBusy(),
    date,
    dateRange,
    search,
    debouncedSearch,
    trayBuckets,
    allTrayEvents,
    calendarCounts,
    handleCalendarSearch,
    setCalendarDate,
    setZoom,
    onAttemptsUpdate,
    handleLoadEvents,
    onDateRangeChange,
    getTrayDataForCalendar,
    resetTrays,
    selectedAttempt,
    setSelectedAttempt,
  };
};

interface IUseCalendarProviderViewModelProps {
  calendarSetId?: string | null;
  calendarId?: string | null;
  selectedUser?: IUserListItemResponse | null;
}
